import { useCallback, useSyncExternalStore } from "react";

/**
 * How far the user has read each Thread, kept in this browser (an unread marker, FR-UI-3).
 * The server keeps no read state; a Thread never opened here counts as unread.
 */
const STORAGE_KEY = "dmw.threads.readUpTo";
const KEPT_THREADS = 500;

type ReadMarks = Readonly<Record<string, number>>;

let marks: ReadMarks = load();
const listeners = new Set<() => void>();

function load(): ReadMarks {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "{}");
    if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
      return Object.fromEntries(
        Object.entries(parsed).filter((pair): pair is [string, number] => typeof pair[1] === "number" && Number.isInteger(pair[1])),
      );
    }
  } catch {
    // Unreadable or blocked storage: start fresh.
  }
  return {};
}

/** Remember that the user has seen the Thread up to `number`; never moves backwards. */
export function markRead(threadId: string, number: number): void {
  if ((marks[threadId] ?? 0) >= number) {
    return;
  }
  const next = Object.entries({ ...marks, [threadId]: number }).slice(-KEPT_THREADS);
  marks = Object.fromEntries(next);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(marks));
  } catch {
    // Storage full or blocked: the marker lives for this page only.
  }
  for (const listener of listeners) {
    listener();
  }
}

/** The last entry number read, or `null` if the Thread was never opened here. */
export function useReadUpTo(threadId: string): number | null {
  const subscribe = useCallback((listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);
  return useSyncExternalStore(subscribe, () => marks[threadId] ?? null);
}
